import { ReservationState, ReservationPerson, ReservationNewPerson, PersonDto, NewPersonDto } from "./types";
import { stringIsNullOrEmpty } from "@/utils/utils";
import { ModelStateList, modelStateListSubscriptionManager } from "@/utils/modelState";
import { CooperativeCancellableLazy } from "../utils/CooperativeCancellableLazy";
import { ConcurrentDictionary } from "../utils/ConcurrentDictionary";

export function isReservationNewPerson(val: ReservationPerson): val is ReservationNewPerson {
    return val && (val as ReservationNewPerson)[ReservationNewPerson] === true;
}

export const ReservationExistingPerson = Symbol("ReservationExistingPerson");
export interface ReservationExistingPerson extends ReservationPerson {
    [ReservationExistingPerson]: boolean;
    PersonId: string;
    ContactId: string | null;
}

export function isReservationExistingPerson(val: ReservationPerson): val is ReservationExistingPerson {
    return val && (val as ReservationExistingPerson)[ReservationExistingPerson] === true;
}

export function cloneReservationPerson(person: ReservationPerson): ReservationPerson {
    // Handle null/undefined - return null to preserve empty person slots
    if (person === null || person === undefined) {
        return null;
    }
    
    if (isReservationExistingPerson(person)) {
        const result: ReservationExistingPerson = {
            [ReservationPerson]: true,
            [ReservationExistingPerson]: true,
            PersonId: person.PersonId,
            ContactId: person.ContactId
        };
        return result;
    }
    
    if (isReservationNewPerson(person)) {
        const result: ReservationNewPerson = {
            [ReservationPerson]: true,
            [ReservationNewPerson]: true,
            FirstName: person.FirstName,
            LastName: person.LastName,
            Email: person.Email,
            Phone: person.Phone
        };
        return result;
    }
    
    // For any other case (unknown person type), return null to avoid crashing
    // This allows cloning to proceed even with unexpected data
    return null;
}



// --- Modified getDisplayName ---
// Now requires reservationState for cache access
export async function getDisplayName(person: ReservationPerson, reservationState: ReservationState): Promise<string> {
    if (!person) {
        return "Unknown";
    }

    if (isReservationExistingPerson(person)) {
        try {
            const personDto = await reservationState.PersonCache.fetchPersonDetails(person.PersonId, reservationState.NewPeople);
            
            if (personDto) {
                // Construct name, handling potential nulls
                const firstName = personDto.FirstName ?? "";
                const lastName = personDto.LastName ?? "";
                const displayName = `${firstName} ${lastName}`.trim();
                return displayName || ""; // Return ID or default if name parts are empty
            } else {
                return ``; // Indicate not found
            }
        } catch (error) {
            // For display name, we can be more lenient - just return a fallback
            console.warn(`Could not fetch display name for person ${person.PersonId}:`, error);
            return `Person ${person.PersonId}`;
        }
    } else if (isReservationNewPerson(person)) {
        // Construct name, handling potential nulls
        const firstName = person.FirstName ?? "";
        const lastName = person.LastName ?? "";
        const displayName = `${firstName} ${lastName}`.trim();
        return displayName || "New Person"; // Default for new person with no name yet
    }

    return ""; // Fallback
}

export interface ReservationPersonDto {
    Type: string;
    Value: ReservationPersonValueDto;
}

export interface ReservationPersonValueDto {
    //
}

export interface ReservationNewPersonValueDto extends ReservationPersonValueDto {
    FirstName: string;
    LastName: string | null;
    Email: string | null;
    Phone: string | null;
}

export interface ReservationExistingPersonValueDto extends ReservationPersonValueDto {
    PersonId: string;
}

export function serializeReservationPerson(person: ReservationPerson): ReservationPersonDto {
    if (!person) {
        return null;
    }

    if (isReservationNewPerson(person)) {
        return {
            Type: "New",
            Value: {
                FirstName: person.FirstName,
                LastName: person.LastName,
                Email: person.Email,
                Phone: person.Phone
            }
        };
    }

    if (isReservationExistingPerson(person)) {
        return {
            Type: "Existing",
            Value: {
                PersonId: person.PersonId
            }
        };
    }

    throw new Error("Invalid person type");
}


// --- Person Details Cache Management ---
// Each reservation instance gets its own cache
export class ReservationPersonCache {
    private cache = new ConcurrentDictionary<string, CooperativeCancellableLazy<PersonDto | null>>();
    private errorCallback?: (error: string) => void;
    
    constructor(errorCallback?: (error: string) => void) {
        this.errorCallback = errorCallback;
    }
    
    async fetchPersonDetails(personId: string, newPeople?: ModelStateList<NewPersonDto>): Promise<PersonDto | null> {
        if (stringIsNullOrEmpty(personId)) {
            return null;
        }

        // Check if this is a temp GUID (new person) - avoid API calls for these
        if (newPeople && isTemporaryPersonId(personId, newPeople)) {
            // Return the PersonDto from the new people list
            const newPerson = newPeople.toArray().find(p => p.PersonId === personId);
            return newPerson ? newPerson.PersonDto : null;
        }

        // Use GetOrAdd to ensure only one CooperativeCancellableLazy is created per personId
        const personLazy = this.cache.getOrAdd(personId, (key) => {
            const lazy = new CooperativeCancellableLazy(async (cancellationToken) => {
                const response = await fetch(`/api/reservations/people/${key}`, {
                    method: 'GET',
                    headers: {
                        'Accept': 'application/json'
                    },
                    signal: cancellationToken.signal
                });

                if (response.status === 404) {
                    const error = `Person with ID ${key} not found (404)`;
                    if (this.errorCallback) {
                        this.errorCallback(`Critical error loading person profile: ${error}`);
                    }
                    throw new Error(error);
                }

                if (!response.ok) {
                    const error = `HTTP error fetching person details! status: ${response.status} ${response.statusText}`;
                    if (this.errorCallback) {
                        this.errorCallback(`Critical error loading person profile: ${error}`);
                    }
                    throw new Error(error);
                }

                return await response.json() as PersonDto;
            });
            
            // Remove from cache on failure so it can be retried
            lazy.getValue().catch(() => {
                this.cache.delete(key);
            });
            
            return lazy;
        });

        return await personLazy.getValue();
    }
    
    // Preload person details if we have them from server
    preloadPersonDetails(personDetails: { [personId: string]: PersonDto }) {
        Object.entries(personDetails).forEach(([personId, personDto]) => {
            if (!this.cache.has(personId)) {
                const lazy = new CooperativeCancellableLazy(async () => personDto);
                this.cache.set(personId, lazy);
            }
        });
    }
    
    clear() {
        this.cache.clear();
    }
}

// --- Fetch Person Details Function ---
export async function fetchPersonDetails(personId: string, newPeople: ModelStateList<NewPersonDto>, cache: ReservationPersonCache): Promise<PersonDto | null> {
    return await cache.fetchPersonDetails(personId, newPeople);
}


// Check if a person ID is a temporary GUID (created in this session)
function isTemporaryPersonId(personId: string, newPeople: ModelStateList<NewPersonDto>): boolean {
    // Check if this person ID exists in the NewPeople list (session-created people)
    return newPeople.toArray().some(p => p.PersonId === personId);
}



// --- Centralized Person Management ---

// Get all people currently referenced in the reservation
export function getAllReferencedPeople(reservationState: ReservationState): Set<string> {
    const referencedPersonIds = new Set<string>();

    // Main reservation person
    if (isReservationExistingPerson(reservationState.Person.value)) {
        referencedPersonIds.add(reservationState.Person.value.PersonId);
    }

    // Room booking people
    for (const roomBooking of reservationState.RoomBookings.toArray()) {
        for (const roomPerson of roomBooking.People.toArray()) {
            if (isReservationExistingPerson(roomPerson.Person.value)) {
                referencedPersonIds.add(roomPerson.Person.value.PersonId);
            }
        }
    }

    // Program booking people
    for (const programBooking of reservationState.ProgramBookings.toArray()) {
        for (const programPerson of programBooking.People.toArray()) {
            if (isReservationExistingPerson(programPerson.Person.value)) {
                referencedPersonIds.add(programPerson.Person.value.PersonId);
            }
        }
    }

    return referencedPersonIds;
}

// Collect all people DTOs from the reservation (for profile picker dropdowns)
export async function collectAllPeopleForDropdown(reservationState: ReservationState): Promise<PersonDto[]> {
    const seenPersonIds = new Set<string>();
    const peopleList: PersonDto[] = [];

    // Get all referenced person IDs
    const referencedPersonIds = getAllReferencedPeople(reservationState);

    // First, add people from NewPeople (session-created people)
    for (const newPerson of reservationState.NewPeople.toArray()) {
        if (!seenPersonIds.has(newPerson.PersonId)) {
            seenPersonIds.add(newPerson.PersonId);
            peopleList.push(newPerson.PersonDto);
        }
    }

    // Then, fetch existing people from API (only those not in NewPeople)
    for (const personId of referencedPersonIds) {
        if (!seenPersonIds.has(personId) && !isTemporaryPersonId(personId, reservationState.NewPeople)) {
            try {
                const personDto = await reservationState.PersonCache.fetchPersonDetails(personId, reservationState.NewPeople);
                if (personDto) {
                    seenPersonIds.add(personId);
                    peopleList.push(personDto);
                }
            } catch (error) {
                // Re-throw critical errors to bubble up and block UI
                throw new Error(`Failed to load person profile for ${personId}: ${error.message}`);
            }
        }
    }

    return peopleList;
}

// Imperative cleanup - remove orphaned new people immediately
export function cleanupOrphanedNewPeople(reservationState: ReservationState): void {
    const referencedPersonIds = getAllReferencedPeople(reservationState);
    const newPeople = reservationState.NewPeople.toArray();

    for (const newPerson of newPeople) {
        if (!referencedPersonIds.has(newPerson.PersonId)) {
            reservationState.NewPeople.remove(newPerson);
        }
    }
}

// Check if a person is new (created in this session)
export function isNewPerson(personId: string, newPeople: ModelStateList<NewPersonDto>): boolean {
    return newPeople.toArray().some(p => p.PersonId === personId);
}

// Setup automatic cleanup subscriptions
export function setupPersonCleanup(reservation: ReservationState): void {
    // Subscribe to changes in person assignments and clean up orphaned new people
    const cleanupSubscriptions = [
        reservation.Person.subscribe(() => cleanupOrphanedNewPeople(reservation)),
        reservation.RoomBookings.subscribe(() => cleanupOrphanedNewPeople(reservation)),
        reservation.ProgramBookings.subscribe(() => cleanupOrphanedNewPeople(reservation))
    ];

    // Subscribe to individual room booking people changes
    const roomSubscription = modelStateListSubscriptionManager(
        reservation.RoomBookings,
        (roomBooking) => {
            const peopleSubscription = roomBooking.People.subscribe(() => {
                cleanupOrphanedNewPeople(reservation);
            });
            return () => peopleSubscription();
        }
    );

    // Subscribe to individual program booking people changes
    const programSubscription = modelStateListSubscriptionManager(
        reservation.ProgramBookings,
        (programBooking) => {
            const peopleSubscription = programBooking.People.subscribe(() => {
                cleanupOrphanedNewPeople(reservation);
            });
            return () => peopleSubscription();
        }
    );

    // Store cleanup function on reservation for proper disposal
    const originalClosed = reservation.closed;
    reservation.closed = function () {
        cleanupSubscriptions.forEach(cleanup => cleanup());
        roomSubscription();
        programSubscription();
        originalClosed.call(this);
    };
}